/* eslint-disable no-underscore-dangle */
/* eslint-disable jsx-a11y/anchor-is-valid */
import 'react-toastify/dist/ReactToastify.css';

import signInArt from '@brands/Auth/SignIn/utils/assets/signinArt.svg';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { getUserByHash } from '@brands/services/auth/getUserByHash';
import { login, LoginResponse } from '@brands/services/auth/login';
import { getBrowserLocationState } from '@brands/services/geolocation/getBrowserLocationState';
import { setUser } from '@brands/store/slices/authSlice';
import { setPatientState } from '@brands/store/slices/currentPatientState';
import { setShowStateModal } from '@brands/store/slices/location';
import { setOrganizationPrices } from '@brands/store/slices/organizationPricingSlice';
import { setOrganization } from '@brands/store/slices/organizationSlice';
import { getCurrentPState, setCurrentPatientState } from '@brands/Utils/localStorage';
import { yupResolver } from '@hookform/resolvers/yup';
import Cookies from 'js-cookie';
import moment from 'moment';
import React, { createContext, FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Button from '../../Components/Button/Button';
import { config } from '../../config/config';
import { useLocalStorageState } from '../../hooks';
import { useAppDispatch } from '../../hooks/useReduxHook';
import { getCurrentOrganization } from '../../services/identity/getCurrentOrganization';
import { getCurrentProfile } from '../../services/identity/getCurrentProfile';
import { UserProfile, UserRoleName } from '../../services/identity/types/UserProfile';
import { getZendeskUrl } from '../../services/zendesk/getZendeskUrl';
import { displayErrorDetails, displayDuplicateClientError, ErrorType } from '../../Utils/displayError';
import { getSecondaryColor } from '../../Utils/getPrimaryColor';
import { cookieOptions } from '../../Utils/getToken';
import { isValidPhoneNumber } from '../../Utils/validation';
import ConfirmOTP from '../ConfirmOTP/ConfirmOTP';
import ForgotPasswordModalConfirmation from '../ForgotPasswordModals/ForgotPasswordModalConfirmation';
import ForgotPasswordModalStepOne from '../ForgotPasswordModals/ForgotPasswordModalStepOne';
import ForgotPasswordModalStepThree from '../ForgotPasswordModals/ForgotPasswordModalStepThree';
import MultiFactorAuthentication from '../MultiFactorAuthentication/MultiFactorAuthentication';
import UpdateEmailModalStepOne from '../UpdateEmail/UpdateEmailModalStepOne';
import UpdateEmailModalStepThree from '../UpdateEmail/UpdateEmailModalStepThree';
import UpdateEmailModalStepTwo from '../UpdateEmail/UpdateEmailModalStepTwo';
import styles from './styles.module.scss';
import { getResetPasswordHashFromUrl } from './utils/resetPasswordLink';
import { validationSchema } from './utils/validationSchema';

type UserSubmitForm = {
  email: string;
  password: string;
};

type ForgotPasswordCredentials = {
  username?: string;
  code: string;
  new_password: string;
  password?: string;
  reset_password_hash?: string;
};

export const JWTContext = createContext('');

type AuthJWT = {
  isAuthenticated: boolean;
};

const ExistingAccount: FC = () => {
  const dispatch = useAppDispatch();
  let phoneNumber = '';
  const [searchParams] = useSearchParams();
  const client = searchParams.get('client');
  const clientAccessCode = searchParams.get('client_access_code');
  const [defaultUser, setDefaultUser] = useState<{ email: string } | null>(null);
  const [passwordShown, setPasswordShown] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(0);
  const [updateEmailStep, setUpdateEmailStep] = useState(0);
  const [signInUserNameError, setSignInUserNameError] = useState(false);
  const [signInPasswordError, setSignInPasswordError] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [resetFlag, setResetFlag] = useState<boolean>(false);
  const [otpModal, setOtpModal] = useState(false);
  const [credentials, setCredentials] = useState<ForgotPasswordCredentials>({
    username: '',
    code: '',
    new_password: '',
  });
  const [showTwoFactorAuth, setShowTwoFactorAuth] = useState<{
    trigger: boolean;
    response: LoginResponse | null;
  }>({ trigger: false, response: null });
  const [, setAuthJWT] = useLocalStorageState<AuthJWT>('isAuthenticated', {
    isAuthenticated: false,
  });

  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const getCurrentLocationForPatient = async (userInfo: UserProfile): Promise<void> => {
    if (userInfo.role.name === UserRoleName.Patient) {
      const storedLocationState = getCurrentPState(userInfo.id);

      const applyState = (state: string, persist = false): void => {
        dispatch(setPatientState({ userId: userInfo.id, state }));
        if (persist) {
          setCurrentPatientState(userInfo.id, state);
        }
        dispatch(setShowStateModal({ showStateModal: state !== storedLocationState }));
      };

      const browserLocationState = await getBrowserLocationState();

      // If the patient picked a state while detection was in flight, their
      // explicit choice wins over anything resolved here — the detected
      // (possibly coarse) location or the stale pre-lookup fallback.
      if (getCurrentPState(userInfo.id) !== storedLocationState) return;

      if (browserLocationState) {
        applyState(browserLocationState, true);
        return;
      }

      if (storedLocationState) {
        applyState(storedLocationState);
        return;
      }

      // No US location and nothing stored: don't guess a state into
      // Redux/localStorage as if it were confirmed — just open the modal (it
      // pre-fills a suggested default for display) and let the patient's
      // explicit choice be the first real write.
      dispatch(setShowStateModal({ showStateModal: true }));
    }
  };

  const goToDashboard = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('return_to') || params.get('redirect_uri');

    if (redirectTo) {
      window.location.replace(redirectTo);
    } else {
      roleNavigate('/dashboard');
    }
  }, [roleNavigate]);

  const goToAdminDashboard = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirect_uri');

    if (redirectTo) {
      window.location.replace(redirectTo);
    } else {
      roleNavigate('/dashboard');
    }
  }, [roleNavigate]);

  const handleZendeskSSO = async (): Promise<void> => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('return_to')) {
        const { url } = await getZendeskUrl(params.get('return_to') || '');
        window.location.href = url;
      }
    } catch {
      displayErrorDetails('Zendesk is not configured');
    }
  };

  const initialize = (): void => {
    try {
      const expired = Cookies.get('jwtTokenExpire');
      if (Number(expired) >= Date.now() / 1000) {
        goToDashboard();
        return;
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    }

    dispatch(setUser({ userInfo: null }));
    dispatch(setOrganization({ currentOrganization: null }));
    dispatch(setOrganizationPrices({ currentOrganizationPrices: null }));
  };

  const togglePassword = (): void => {
    setPasswordShown(!passwordShown);
  };

  const getCurrentLocation = (userInfo: UserProfile): void => {
    if (
      userInfo.role.name === UserRoleName.Admin ||
      userInfo.role.name === UserRoleName.SuperAdmin ||
      userInfo.role.name === UserRoleName.OrganizationAdmin
    ) {
      goToAdminDashboard();
    } else {
      if (userInfo.role.name === UserRoleName.Patient) {
        void getCurrentLocationForPatient(userInfo);
      }
      goToDashboard();
    }
  };

  useEffect(() => {
    (async (): Promise<void> => {
      const resetPasswordHash = getResetPasswordHashFromUrl();

      if (resetPasswordHash) {
        try {
          await getUserByHash(resetPasswordHash);
          setResetFlag(true);
          setForgotPasswordStep(3);
          setCredentials({
            reset_password_hash: resetPasswordHash,
            code: '',
            new_password: '',
            password: '',
          });
        } catch (error) {
          displayErrorDetails(error);
        }
      }
    })();
  }, []);

  const renderForgotPasswordModal = (): JSX.Element | null => {
    switch (forgotPasswordStep) {
      case 1:
        return (
          <ForgotPasswordModalStepOne
            forgotPasswordStep={forgotPasswordStep}
            setForgotPasswordStep={setForgotPasswordStep}
            setUpdateEmailStep={setUpdateEmailStep}
            credentials={credentials}
            setCredentials={setCredentials}
          />
        );
      case 3:
        return (
          <ForgotPasswordModalStepThree
            forgotPasswordStep={forgotPasswordStep}
            setForgotPasswordStep={setForgotPasswordStep}
            credentials={credentials}
            setCredentials={setCredentials}
            resetFlag={resetFlag}
            triggerLogin={triggerLogin}
          />
        );
      case 5:
        return (
          <ForgotPasswordModalConfirmation
            forgotPasswordStep={forgotPasswordStep}
            setForgotPasswordStep={setForgotPasswordStep}
            credentials={credentials}
            setCredentials={setCredentials}
          />
        );
      default:
        return null;
    }
  };

  const renderUpdateEmailModal = (): JSX.Element | null => {
    switch (updateEmailStep) {
      case 1:
        return (
          <UpdateEmailModalStepOne
            setUpdateEmailStep={setUpdateEmailStep}
            updateEmailStep={updateEmailStep}
            setCredentials={setCredentials}
          />
        );
      case 2:
        return (
          <UpdateEmailModalStepTwo
            updateEmailStep={updateEmailStep}
            setUpdateEmailStep={setUpdateEmailStep}
            credentials={credentials}
            setCredentials={setCredentials}
          />
        );
      case 3:
        return (
          <UpdateEmailModalStepThree
            setUpdateEmailStep={setUpdateEmailStep}
            updateEmailStep={updateEmailStep}
            credentials={credentials}
            setCredentials={setCredentials}
          />
        );
      default:
        return null;
    }
  };

  const dispatchSuccessEvent = (userId: string): void => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'login',
      userId,
    });
  };

  const onConfirmedOTP = (): void => {
    setOtpModal(false);
    setLoading(false);
  };

  const triggerLogin = async (response: LoginResponse): Promise<void> => {
    const accessToken = response.access_token;
    const refreshToken = response.refresh_token;

    // Set JWT token as a cookie for the root domain
    Cookies.set('jwtToken', accessToken as string, cookieOptions());
    Cookies.set('refreshToken', refreshToken as string, cookieOptions());

    // Need to be removed so that we can handle token expiration in a better way
    Cookies.set('jwtTokenExpire', moment().add(20, 'hours').valueOf().toString(), cookieOptions());

    const newJWT: AuthJWT = {
      isAuthenticated: true,
    };
    setAuthJWT(newJWT);
    if (response.redirect_url && typeof window !== 'undefined' && config.organization_id === 'dentistry-one') {
      window.location.href = response.redirect_url;
    }
    const userDetails = await getCurrentProfile(response.role);
    await handleZendeskSSO();
    dispatch(setUser({ userInfo: userDetails }));

    if (
      userDetails.role.name !== UserRoleName.Admin &&
      userDetails.role.name !== UserRoleName.SuperAdmin &&
      userDetails.role.name !== UserRoleName.OrganizationAdmin
    ) {
      const currentOrganization = await getCurrentOrganization();
      dispatch(setOrganization({ currentOrganization }));
    }

    dispatchSuccessEvent(userDetails.id.toString());

    getCurrentLocation(userDetails);
  };

  const endLogin = (data: UserSubmitForm, error: unknown): void => {
    setLoading(false);
    if (error as ErrorType) {
      const { response } = error as ErrorType;
      if (response?.data?.message?.includes('Password reset is required')) {
        setResetFlag(true);
        setCredentials({
          ...credentials,
          username: isValidPhoneNumber(data.email) ? `+1${phoneNumber}` : data.email.toLowerCase(),
        });
        setForgotPasswordStep(1);
        return;
      }
      if (response?.data?.message?.includes('User is not confirmed')) {
        setCredentials({
          ...credentials,
          password: data.password,
          username: isValidPhoneNumber(data.email) ? `+1${phoneNumber}` : data.email.toLowerCase(),
        });
        setOtpModal(true);
        return;
      }
      if (displayDuplicateClientError(error)) return;
      displayErrorDetails(error);
      if (
        response?.data?.message?.includes('User is not found') ||
        response?.data?.message?.includes('User is not active')
      ) {
        setSignInUserNameError(true);
        setSignInPasswordError(false);
      } else {
        setSignInUserNameError(false);
        if (response?.data?.message?.includes('Invalid credential')) {
          setSignInPasswordError(true);
        }
      }
    } else {
      setSignInUserNameError(false);
      setSignInPasswordError(false);
    }
  };

  const onSubmit = async (data: UserSubmitForm): Promise<void> => {
    try {
      setLoading(true);
      if (isValidPhoneNumber(data.email)) {
        phoneNumber = data.email.replace(/\s+|[()-.]/g, '');
      }
      const response = await login({
        email: isValidPhoneNumber(data.email) ? `+1${phoneNumber}` : data.email.toLowerCase(),
        password: data.password,
        organization_id: config.organization_id,
        ...(config.division_id && { division_id: config.division_id }),
        ...(config.practice_id && { practice_id: config.practice_id }),
        ...(client && { client }),
        ...(clientAccessCode && { client_access_code: clientAccessCode }),
        handle_existing_user: true,
      });

      if (response.mfa_method) {
        setShowTwoFactorAuth({
          trigger: true,
          response,
        });
      } else if (response.access_token && response.refresh_token) {
        await triggerLogin(response);
      }
    } catch (error: unknown) {
      endLogin(data, error);
    }
  };

  useEffect(() => {
    renderForgotPasswordModal();
  }, [forgotPasswordStep]);

  useEffect(() => {
    initialize();
    localStorage.removeItem('sign-up-form');
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    getValues,
  } = useForm<UserSubmitForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    const emailParam = searchParams.get('email');

    if (emailParam) {
      const correctedEmail = emailParam.trim().replace(/\s/g, '+');

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const emailValid = emailRegex.test(correctedEmail);
      if (emailValid) {
        setValue('email', correctedEmail);
        setDefaultUser({ email: correctedEmail });
      }
    }
  }, [searchParams, setValue]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>, inputType: 'email' | 'password'): void => {
    clearErrors(inputType);
    const { value } = e.target;
    setSignInUserNameError(false);
    setSignInPasswordError(false);
    setValue(inputType, value);
  };

  if (otpModal) {
    return <ConfirmOTP username={credentials.username as string} onConfirmed={onConfirmedOTP} />;
  }

  return (
    <div>
      {showTwoFactorAuth.trigger ? (
        <MultiFactorAuthentication
          loginResponse={showTwoFactorAuth.response!}
          setShowTwoFactorAuth={setShowTwoFactorAuth}
          userCredentials={{
            email: getValues('email')?.toLowerCase(),
            password: getValues('password'),
          }}
          triggerLogin={triggerLogin}
          endLogin={endLogin}
        />
      ) : (
        <>
          {renderForgotPasswordModal()}
          {renderUpdateEmailModal()}
          <Sidenav isVisibleOnMobile hideDefaultOptions>
            <a
              href="https://dentistryone.zendesk.com/hc/en-us/articles/11914427760151-How-to-change-your-password"
              target="_blank"
              className={styles.sidenavContainer}
              rel="noreferrer"
              style={{ textDecoration: 'none' }}
            >
              <div className={styles.sidenavToggleText}>Help</div>
              <div className={styles.sidenavToggle}>
                <AiOutlineQuestionCircle />
              </div>
            </a>
          </Sidenav>
          <div className={styles.body}>
            <div className={styles.leftSection}>
              <div className={styles.container}>
                <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
                  <span className={styles.formTitle}>
                    <span className={`${styles.formTitleWelcome} fs-unmask`}>Existing Account</span>
                    <div className={styles.formTitleLink}>
                      <span className={`${styles.existTxt1} fs-unmask`}>
                        This email address already has a Dentistry.One account. Log in below to add these benefits to
                        your account. If this isn't your account,{' '}
                        <a className={styles.clickHere} onClick={() => navigate(-1)}>
                          click here
                        </a>{' '}
                        to go back and use a different email address.
                      </span>
                    </div>
                  </span>
                  {!defaultUser ? (
                    <div className={styles.inputWrap}>
                      <TextField
                        {...register('email')}
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                        type="text"
                        name="email"
                        onChange={(value) => handleOnChange(value, 'email')}
                        label="Email"
                        errors={errors.email ? errors : {}}
                      />
                    </div>
                  ) : (
                    <div className={styles.inputWrap}>
                      <TextField
                        {...register('email')}
                        type="text"
                        name="email"
                        disabled
                        label="Email"
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                      />
                    </div>
                  )}
                  <div className={styles.inputWrap}>
                    <div className={styles.inputWrapper}>
                      <TextField
                        {...register('password')}
                        data-testid="signin-password-input"
                        label="Password"
                        required
                        type="password"
                        showPasswordToggle
                        name="password"
                        onChange={(e) => {
                          clearErrors('password');
                          const { value } = e.target;
                          setValue('password', value);
                        }}
                        errors={errors.password ? errors : {}}
                        inputClass={`${styles.profileInputClass} fs-exclude`}
                        wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
                      />
                    </div>
                  </div>
                  <div
                    className={`${styles.formCheck} fs-unmask`}
                    style={{ paddingLeft: '0px' }}
                    onClick={() => setForgotPasswordStep(1)}
                  >
                    Forgot password?
                  </div>
                  <Button
                    type="submit"
                    className={`${loading && styles.inActiveBtn} fs-unmask`}
                    style={{ borderRadius: '4px', height: '56px' }}
                  >
                    Login
                  </Button>
                </form>
              </div>
            </div>
            <div className={styles.rightImage}>
              <div
                className={`${styles.cardImage} ${styles.signinArt}`}
                style={{ backgroundImage: `url(${signInArt})` }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExistingAccount;
